import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import {
  RedoTable,
  RedoTableColumn,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { VariantInfo, VariantMetrics } from "./ab-test";

// A/B Test Report Modal Component
interface ABTestReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  variants: VariantInfo[];
  metricsData: VariantMetrics[];
  isLoading: boolean;
}

// Define the report data type for table rows
interface ReportRow {
  id: string;
  name: string;
  traffic: number;
  sends: number;
  openRate: number;
  clickRate: number;
  conversionRate: number;
  revenue: number;
}

export const ABTestReportModal = memo(function ABTestReportModal({
  isOpen,
  onClose,
  variants,
  metricsData,
  isLoading,
}: ABTestReportModalProps) {
  // Function to format currency
  const formatCurrency = (value: number) => {
    return `$${value.toFixed(2)}`;
  };

  // Function to format percentage
  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  // Calculate traffic distribution based on variant weights
  const totalWeight = variants.reduce(
    (acc, variant) => acc + variant.weight,
    0,
  );

  // Get the metric data for each variant or create empty data
  const reportData: ReportRow[] = variants.map((variant) => {
    const metrics = metricsData.find((m) => m.variant.id === variant.id);
    const traffic = totalWeight > 0 ? (variant.weight / totalWeight) * 100 : 0;

    return {
      id: variant.id,
      name: variant.name,
      traffic,
      sends: metrics?.metrics?.sends || 0,
      openRate: metrics?.openRate || 0,
      clickRate: metrics?.clickRate || 0,
      conversionRate: metrics?.conversionRate || 0,
      revenue: metrics?.revenue || 0,
    };
  });

  // Find the best performer for each metric
  const bestPerformers = useMemo(() => {
    if (reportData.length === 0) {
      return {};
    }

    const validData = reportData.filter(
      (row) => row.sends > 0 && row.openRate > 0,
    );

    if (validData.length === 0) {
      return {};
    }

    return {
      openRate: validData.reduce((prev, current) =>
        prev.openRate > current.openRate ? prev : current,
      ).id,
      clickRate: validData.reduce((prev, current) =>
        prev.clickRate > current.clickRate ? prev : current,
      ).id,
      conversionRate: validData.reduce((prev, current) =>
        prev.conversionRate > current.conversionRate ? prev : current,
      ).id,
      revenue: validData.reduce((prev, current) =>
        prev.revenue > current.revenue ? prev : current,
      ).id,
    };
  }, [reportData]);

  // Helper function to create styled cell
  const createStyledCell = (
    row: ReportRow,
    metricKey: keyof typeof bestPerformers,
    content: string,
  ) => {
    const isBest = bestPerformers[metricKey] === row.id;

    return (
      <RedoTableTextCell
        text={content}
        textColor={isBest ? "success" : undefined}
      />
    );
  };

  // Define table columns
  const tableColumns: RedoTableColumn<ReportRow>[] = [
    {
      key: "name",
      widthMode: { type: "fixed", width: 150 },
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Variation" />
      ),
      renderNormalCell: ({ row }) => <RedoTableTextCell text={row.name} />,
    },
    {
      key: "traffic",
      widthMode: { type: "fixed", width: 100 },
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Traffic" />
      ),
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell text={formatPercentage(row.traffic)} />
      ),
    },
    {
      key: "sends",
      widthMode: { type: "fixed", width: 100 },
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Sends" />
      ),
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell text={row.sends.toString()} />
      ),
    },
    {
      key: "openRate",
      widthMode: { type: "fixed", width: 100 },
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Open rate" />
      ),
      renderNormalCell: ({ row }) =>
        createStyledCell(row, "openRate", formatPercentage(row.openRate)),
    },
    {
      key: "clickRate",
      widthMode: { type: "fixed", width: 100 },
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Click rate" />
      ),
      renderNormalCell: ({ row }) =>
        createStyledCell(row, "clickRate", formatPercentage(row.clickRate)),
    },
    {
      key: "conversionRate",
      widthMode: { type: "fixed", width: 150 },
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Conversion rate" />
      ),
      renderNormalCell: ({ row }) =>
        createStyledCell(
          row,
          "conversionRate",
          formatPercentage(row.conversionRate),
        ),
    },
    {
      key: "revenue",
      widthMode: { type: "fixed", width: 120 },
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Revenue" />
      ),
      renderNormalCell: ({ row }) =>
        createStyledCell(row, "revenue", formatCurrency(row.revenue)),
    },
  ];

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="lg"
      onModalCloseRequested={onClose}
      primaryButton={{ text: "Close", onClick: onClose }}
      title="Weighted branch report"
    >
      {isLoading ? (
        <Flex justify="center" p="md">
          <Text fontSize="sm">Loading metrics data...</Text>
        </Flex>
      ) : reportData.length === 0 ? (
        <Flex justify="center" p="md">
          <Text fontSize="sm">No variants available to display.</Text>
        </Flex>
      ) : (
        <Flex dir="column" p="md">
          <RedoTable<ReportRow>
            layout={{ columns: tableColumns, headerColor: "white" }}
            rowsOfData={reportData}
            size="md"
          />
        </Flex>
      )}
    </RedoModal>
  );
});
